import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getPlusContactUrl, PLUS_CONTACT_EMAIL } from "../services/billing.js";

const PlusContext = createContext(null);
const MOCK_PLUS_KEY = "sielpa-plus:mock-plus:v1";
const VOCAB_BURST_PREVIEW_LIMIT = 5;

function todayKey(name) {
  return `sielpa-plus:${name}:${new Date().toISOString().slice(0, 10)}`;
}

function readBoolean(key) {
  try {
    return window.localStorage.getItem(key) === "true";
  } catch {
    return false;
  }
}

function readCount(name) {
  try {
    return Number.parseInt(window.localStorage.getItem(todayKey(name)) || "0", 10) || 0;
  } catch {
    return 0;
  }
}

function writeStored(key, value) {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // Local storage is optional; retain the in-memory practice state.
  }
}

export function PlusProvider({ children }) {
  const [mockPlus, setMockPlus] = useState(() => readBoolean(MOCK_PLUS_KEY));
  const [vocabBurstPreviewUsed, setVocabBurstPreviewUsed] = useState(() => readCount("vocabBurst"));

  useEffect(() => {
    // Old checkout/portal return links must not start billing-status polling.
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has("sielpa_plus")) {
        url.searchParams.delete("sielpa_plus");
        window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
      }
    } catch {
      // The practice tools still work when history access is unavailable.
    }
  }, []);

  const value = useMemo(() => {
    const isPlus = mockPlus;
    const previewRemaining = Math.max(0, VOCAB_BURST_PREVIEW_LIMIT - vocabBurstPreviewUsed);

    function setTestPlus(enabled) {
      setMockPlus(enabled);
      writeStored(MOCK_PLUS_KEY, enabled);
    }

    function consumeVocabBurstPreview() {
      if (isPlus) {
        return { ok: true, used: vocabBurstPreviewUsed, limit: VOCAB_BURST_PREVIEW_LIMIT, remaining: previewRemaining, plus: true };
      }
      if (vocabBurstPreviewUsed >= VOCAB_BURST_PREVIEW_LIMIT) {
        return { ok: false, used: vocabBurstPreviewUsed, limit: VOCAB_BURST_PREVIEW_LIMIT, remaining: 0, plus: false };
      }
      const nextUsed = vocabBurstPreviewUsed + 1;
      setVocabBurstPreviewUsed(nextUsed);
      writeStored(todayKey("vocabBurst"), nextUsed);
      return {
        ok: true,
        used: nextUsed,
        limit: VOCAB_BURST_PREVIEW_LIMIT,
        remaining: Math.max(0, VOCAB_BURST_PREVIEW_LIMIT - nextUsed),
        plus: false
      };
    }

    return {
      isPlus,
      enquiryOnly: true,
      isAdFree: isPlus,
      canUseVocabBurst: isPlus || previewRemaining > 0,
      vocabBurstPreviewLimit: VOCAB_BURST_PREVIEW_LIMIT,
      vocabBurstPreviewUsed,
      vocabBurstPreviewRemaining: previewRemaining,
      contactEmail: PLUS_CONTACT_EMAIL,
      contactUrl: getPlusContactUrl(),
      setTestPlus,
      consumeVocabBurstPreview
    };
  }, [mockPlus, vocabBurstPreviewUsed]);

  return <PlusContext.Provider value={value}>{children}</PlusContext.Provider>;
}

export function usePlus() {
  const context = useContext(PlusContext);
  if (!context) throw new Error("usePlus must be used inside PlusProvider.");
  return context;
}
